import { Navigate, Route, Routes } from 'react-router-dom'
import { getPages, Page } from './app-menu'
import { useAuth } from '../auth/auth-hooks'
import { UserType } from '../../api/auth-api'
import { useMemo } from 'react'
import { ProposalsProvider } from '../buyer/proposal-context.tsx'
import { IssuersProvider } from '../buyer/issuers-context.tsx'
import { Box } from '@mui/material'

interface AppContainerProps {
  isInitialized: boolean
}

export default function AppContainer({ isInitialized }: AppContainerProps) {
  const { userType, isLoading, error } = useAuth()

  const pages = useMemo(() => {
    return !isLoading && userType ? getPages(userType) : []
  }, [userType, isLoading])

  const getContent = () => {
    // Error state
    if (error) {
      return (
        <Box mt={2} ml={2}>
          <h2>Error</h2>
          <p>{error}</p>
        </Box>
      )
    }

    // Access denied state
    if (isInitialized && !isLoading && userType === UserType.NOT_VALID) {
      return (
        <Box mt={2} ml={2}>
          <h2>Not Authorized</h2>
          <p>
            Please contact your administrator to gain access to this
            application.
          </p>
        </Box>
      )
    }

    // Loading state
    if (!isInitialized || isLoading) {
      return (
        <Box mt={2} ml={2}>
          <p>Loading...</p>
        </Box>
      )
    }

    // Create routes content for rendering
    const routesContent = (
      <Routes>
        {pages.map((page: Page) => (
          <Route
            path={page.routerPath ?? page.path}
            Component={page.component}
            key={page.path}
          />
        ))}
        {pages.length > 0 && (
          <Route path="*" element={<Navigate to={pages[0].path} />} />
        )}
      </Routes>
    )

    // Wrap routes in appropriate providers based on user type
    if (userType === UserType.BUYER) {
      return (
        <IssuersProvider>
          <ProposalsProvider>{routesContent}</ProposalsProvider>
        </IssuersProvider>
      )
    }

    // For ISSUER or any other valid user types that don't need special providers
    return routesContent
  }

  // Consistent layout wrapper for all content states
  return (
    <div id="app-body">
      <div id="app-page">{getContent()}</div>
    </div>
  )
}
